<template>
    <div class="flex items-center">
        <Tooltip placement="top" max-width="350">
            <template #trigger>
                <span class="cursor-help rounded bg-slate-100 text-slate-600 px-2 py-0.5">
                    {{ isSponsored ? 'Sponsored' : 'Organic' }}
                </span>
            </template>
            <template #default>
                <div class="text-xs space-y-2">
                    <!-- AI Analysis exists -->
                    <template v-if="analysis">
                        <div class="font-semibold text-white border-b border-slate-600 pb-1 mb-2">
                            AI Sponsorship Analysis
                        </div>

                        <div class="space-y-1.5">
                            <div class="flex justify-between">
                                <span class="text-slate-400">AI Result:</span>
                                <span class="'text-slate-300'">
                                    {{ analysis.is_sponsored ? 'Sponsored' : 'Not Sponsored' }}
                                </span>
                            </div>

                            <div v-if="analysis.sponsorship_type" class="flex justify-between">
                                <span class="text-slate-400">Type:</span>
                                <span class="text-white capitalize">{{ formatSponsorshipType(analysis.sponsorship_type) }}</span>
                            </div>

                            <div v-if="analysis.sponsor_type" class="flex justify-between">
                                <span class="text-slate-400">Sponsor:</span>
                                <span class="text-white capitalize">{{ analysis.sponsor_type }}</span>
                            </div>

                            <div v-if="analysis.name_of_sponsor_product" class="flex justify-between">
                                <span class="text-slate-400">Product:</span>
                                <span class="text-white">{{ analysis.name_of_sponsor_product }}</span>
                            </div>

                            <div v-if="analysis.confidence" class="flex justify-between">
                                <span class="text-slate-400">Confidence:</span>
                                <span :class="confidenceClass">{{ Math.round(analysis.confidence * 100) }}%</span>
                            </div>

                            <div v-if="analysis.is_creator_program" class="flex justify-between">
                                <span class="text-slate-400">Creator Program:</span>
                                <span class="text-amber-400">Yes</span>
                            </div>

                            <div v-if="analysis.analyzed_at" class="flex justify-between pt-1 border-t border-slate-600 mt-1">
                                <span class="text-slate-400">Analyzed:</span>
                                <span class="text-slate-300">{{ formatDate(analysis.analyzed_at) }}</span>
                            </div>
                        </div>

                        <div v-if="hasOverride" class="mt-2 pt-2 border-t border-amber-600/50 text-amber-400 text-xs">
                            <AppIcon name="pen" class="mr-1 h-3 w-3" />
                            Status was manually overridden
                        </div>
                    </template>

                    <!-- No AI Analysis -->
                    <template v-else>
                        <div class="font-semibold text-white border-b border-slate-600 pb-1 mb-2">
                            No AI Analysis
                        </div>
                        <p class="text-slate-300">
                            This content was not analyzed by AI because no sponsorship indicators
                            (such as #ad, #sponsored, or similar tags) were detected in the title or description.
                        </p>
                        <p class="text-slate-400 text-xs mt-2">
                            Content is only sent for AI analysis when initial keyword detection suggests it may be sponsored.
                        </p>
                    </template>
                </div>
            </template>
        </Tooltip>

        <!-- Override indicator -->
        <span v-if="hasOverride" class="ml-1.5 text-xs text-amber-600">(edited)</span>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'
import Tooltip from '@/components/Tooltip.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps({
    isSponsored: {
        type: Boolean,
        required: true,
    },
    analysis: {
        type: Object,
        default: null,
    },
    isSponsorshipOverridden: {
        type: Boolean,
        default: false,
    },
})

const hasOverride = computed(() => {
    return props.isSponsorshipOverridden
})

const confidenceClass = computed(() => {
    if (!props.analysis?.confidence) return 'text-white'
    if (props.analysis.confidence >= 0.9) return 'text-emerald-400'
    if (props.analysis.confidence >= 0.7) return 'text-amber-400'
    return 'text-red-400'
})

const formatSponsorshipType = (type) => {
    if (!type) return ''
    return type.replace(/_/g, ' ')
}

const formatDate = (date) => {
    if (!date) return ''
    return moment(date).format('MMM D, YYYY h:mm A')
}
</script>


